import { Box, useToken } from '@chakra-ui/react';
import { useCallback, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftTruncatedTextWithTooltip from '@/components/Atoms/TruncatedTextWithTooltip';
import FaceliftDialogWithExitConfirmation from '@/components/Molecules/FaceliftDialogWithExitConfirmation';
import DataTable from '@/components/Organisms/DataTable';
import type { onFilterApplyProps } from '@/components/Organisms/DataTable/DataTable.types';
import { useGetGraftsToAssign } from '@/hooks/service-hooks/graftsToAssign';
import { useGraftsToAssignTutorial } from '@/hooks/tutorialsHooks/useGraftLogsTutorial';
import { useClinicFilter } from '@/hooks/useClinicFilter';
import usePermission from '@/hooks/usePermission';
import { getGraftsToAssigned } from '@/services/graftsToAssigned';
import { useAuthStore } from '@/store';
import { useTourContext } from '@/store/tourContext';
import type { IUnassignedTissue } from '@/types/graftsToAssigned';
import type { FilterType } from '@/types/table';
import { formatDate } from '@/utils';
import {
	generateCSVFromTable,
	generateExcelFromTable,
} from '@/utils/exportFiles';
import { generateMockUnassignedTissueData } from '@/utils/tutorials/mockData';
import { EditForm } from '../EditForm';
import { UtilityForm } from '../Form';
import { columns } from './column';

const CLINIC_KEY = '_ta_clinic_value';

const GraftsToAssignTable = () => {
	const graftsToAssignTableRef = useRef<HTMLDivElement | null>(null);
	// Modal state management
	const { hasPermission } = usePermission();
	const [brand600] = useToken('colors', ['brand.600']);
	const [isFormDirty, setIsFormDirty] = useState(false);
	const [modalInformation, setModalInformation] = useState<{
		mode: 'create' | 'edit' | 'details' | null;
		data: string | Partial<IUnassignedTissue> | null;
		element: React.ReactNode | null;
	}>({
		mode: null,
		data: null,
		element: null,
	});

	const user = useAuthStore((state) => state.user);

	const { defaultClinicNames, defaultClinicIds, mapGlobalFilters } = useClinicFilter(
		CLINIC_KEY,
		true,
	);

	const portsAvailable =
		user?.userInformation?.portsAvailable?.trim()?.toLowerCase() || null;

	const allColumns = useMemo(() => {
		if (
			hasPermission(['GraftsToAssign:Edit']) &&
			portsAvailable !== 'salesrep'
		) {
			return columns;
		}
		return columns.filter((item) => item.key !== 'assign');
	}, [hasPermission, portsAvailable]);
	

	const [filters, setFilters] = useState<FilterType>({
		page: 1,
		limit: 15,
		columnsFilters: '[]',
		search: '',
		enabled: defaultClinicNames.length > 0,
		status: '',
		globalFilters: JSON.stringify(
			defaultClinicNames.length
				? [
						{
							key: CLINIC_KEY,
							values: defaultClinicNames,
						},
						{
							key: '_ta_clinic_ids',
							values: defaultClinicIds,
						}
					]
				: [],
		),
		sort: '',
		port: user?.userInformation?.portsAvailable?.trim() || '',
	});
	// Register tutorial refs
	useGraftsToAssignTutorial({
		graftsToAssignTableRef,
	});
	const { activeTutorialName } = useTourContext();
	const isGraftLogsTutorialActive = activeTutorialName === 'OnboardingTutorialGraftLogs';
	const { data, isFetching, refetch } = useGetGraftsToAssign(filters);

	// Define handleEditGraftsToAssign before useMemo to avoid initialization error
	const handleEditGraftsToAssign = useCallback(
		(item: any) => {
			setIsFormDirty(false);
			setModalInformation({
				mode: 'edit',
				data: item,
				element: (
					<EditForm
						initialData={item}
						onSuccess={() => {
							setModalInformation({ mode: null, data: null, element: null });
							setIsFormDirty(false);
							refetch();
						}}
						isLoading={false}
						onIsDirtyChange={setIsFormDirty}
					/>
				),
			});
		},
		[refetch],
	);

	// Handle filter apply
	const onFilterApply = useCallback(
		({
			page,
			limit,
			status,
			search = '',
			sort = null,
			columnsFilters = [],
			globalFilters = [],
		}: onFilterApplyProps) => {
			// Map global filters, with clinic names in this case
			const mappedFilters = mapGlobalFilters(globalFilters || [], true);
			const modifiedGlobalFilters = [
				...mappedFilters,
				{
					key: '_ta_clinic_ids',
					values: defaultClinicIds
				}
			];

			// Update pagination state to trigger API call
			setFilters({
				page,
				limit,
				columnsFilters: JSON.stringify(columnsFilters),
				search,
				enabled: defaultClinicNames.length > 0,
				status: status?.value,
				globalFilters: JSON.stringify(modifiedGlobalFilters),
				sort: sort ? `${sort.key}|${sort.direction}` : '',
				port: user?.userInformation?.portsAvailable?.trim() || '',
			});
		},
		[mapGlobalFilters],
	);

	const transformedData = useMemo(() => {
		// IMPORTANT: Only use mock data when the GraftLogs tutorial is ACTIVE
		// When tutorial is NOT active, use real API data
		if (isGraftLogsTutorialActive) {
			// Tutorial is active - use mock data
			const mockData = generateMockUnassignedTissueData(10);
			return mockData.map((item) => {
				return {
					id: '',
					physician: (
						<FaceliftTruncatedTextWithTooltip
							text={item.physician || '-'}
							maxLength={20}
						/>
					),
					patient: (
						<FaceliftTruncatedTextWithTooltip
							text={item.patient || '-'}
							maxLength={20}
						/>
					),
					proceduredate: formatDate(item.proceduredate) || '-',
					tissueid: item.tissueid || '-',
					product: item.product || '-',
					size: item.size || '-',
					assign: (
						<FaceliftButton
							size={'2xs'}
							border={'none'}
							variant={'outline'}
							onClick={() => {
								handleEditGraftsToAssign(item);
							}}
						>
							Assign
						</FaceliftButton>
					),
				};
			});
		}

		if (!data?.items) return [];

		return data?.items.map((item) => {
			return {
				id: '',
				physician: (
					<FaceliftTruncatedTextWithTooltip
						text={item.physician || '-'}
						maxLength={20}
					/>
				),
				patient: (
					<FaceliftTruncatedTextWithTooltip
						text={item.patient || '-'}
						maxLength={20}
					/>
				),
				proceduredate: formatDate(item.proceduredate) || '-',
				tissueid: item.tissueid || '-',
				product: item.product || '-',
				size: item.size || '-',
				assign: (
					<FaceliftButton
						size={'2xs'}
						border={'none'}
						variant={'outline'}
						onClick={() => {
							handleEditGraftsToAssign(item);
						}}
					>
						Assign
					</FaceliftButton>
				),
			};
		});
	}, [data, isGraftLogsTutorialActive, handleEditGraftsToAssign]);

	const transformedDataToExport = (data: IUnassignedTissue[]) => {
		if (!data) return [];

		return data.map((item) => {
			return {
				physician: item.physician || '-',
				patient: item.patient || '-',
				proceduredate: formatDate(item.proceduredate) || '-',
				tissueid: item.tissueid || '-',
				product: item.product || '-',
				size: item.size || '-',
			};
		});
	};

	// Export functions
	const exportAllAsCSV = async () => {
		try {
			// Fetch all data with current filters
			const data = await getGraftsToAssigned({
				page: 1,
				limit: 9999,
				columnsFilters: '[]',
				search: filters.search || '',
				globalFilters: filters.globalFilters,
				sort: filters.sort ? filters.sort : '',
			});

			const transformedData = transformedDataToExport(data?.items || []);
			generateCSVFromTable(
				columns.filter((item) => item.key !== 'assign'),
				transformedData,
				`UnAssigned-all`,
				true,
			);
		} catch (error) {
			console.error('Error exporting Csv:', error);
			toast.error('Failed to export Csv');
		}
	};

	const exportAllAsExcel = async () => {
		try {
			// Fetch all data with current filters
			const data = await getGraftsToAssigned({
				page: 1,
				limit: 9999,
				columnsFilters: '[]',
				search: filters.search || '',
				globalFilters: filters.globalFilters,
				sort: filters.sort ? filters.sort : '',
			});

			const transformedData = transformedDataToExport(data?.items || []);
			generateExcelFromTable(
				columns.filter((item) => item.key !== 'assign'),
				transformedData,
				`UnAssigned-all`,
				true,
			);
		} catch (error) {
			console.error('Error exporting Excel:', error);
			toast.error('Failed to export Excel');
		}
	};

	const handleCloseModal = () => {
		setModalInformation({ mode: null, data: null, element: null });
		setIsFormDirty(false);
	};

	const handleCreateGraftsToAssign = () => {
		setIsFormDirty(false);
		setModalInformation({
			mode: 'create',
			data: null,
			element: (
				<UtilityForm
					mode={modalInformation.mode as 'create'}
					initialData={modalInformation.data}
					onSuccess={() => {
						setModalInformation({ mode: null, data: null, element: null });
						setIsFormDirty(false);
						refetch();
					}}
					isLoading={false}
					onIsDirtyChange={setIsFormDirty}
				/>
			),
		});
	};

	return (
		<>
			<Box
				p={{ base: 4, md: 8 }}
				bg={{ base: '{colors.gray.50}', _dark: '{colors.gray.900}' }}
			>
				<DataTable
					ref={graftsToAssignTableRef}
					tableName="Grafts To Assign"
					loading={isFetching}
					items={transformedData}
					columns={allColumns}
					topLabel="Grafts To Assign"
					totalCount={data?.meta.totalRecords || 0}
					onFilterApply={onFilterApply}
					searchable
					bordered
					hideShowColumn
					expandable
					paginated
					fullscreen
					exportable
					globalFilter={{
						title: 'Filters',
						items: [
							{
								options: defaultClinicNames.map((clinic) => ({
									label: clinic,
									id: clinic,
								})),
								key: 'Clinics',
								name: 'Clinics',
							},
						],
					}}
					{...(hasPermission(['GraftsToAssign:Create']) &&
						portsAvailable !== 'salesrep' && {
							creatableFn: handleCreateGraftsToAssign,
						})}
					csv
					excel
					exportAllAsCSV={exportAllAsCSV}
					exportAllAsExcel={exportAllAsExcel}
				/>
			</Box>

			<FaceliftDialogWithExitConfirmation
				showBackBtn={true}
				open={
					modalInformation.mode === 'create' || modalInformation.mode === 'edit'
				}
				onOpenChange={(open) => {
					if (!open) handleCloseModal();
				}}
				title={
					modalInformation.mode === 'create'
						? 'Add New Graft Log'
						: 'Edit Graft Log'
				}
				size="xl"
				sx={{
					width: 700,
					maxWidth: '95vw',
					maxHeight: '85vh',
					boxShadow: `0 0 20px 0px ${brand600}`,
					overflow: 'visible',
				}}
				isDirty={isFormDirty}
			>
				{modalInformation.element}
			</FaceliftDialogWithExitConfirmation>
		</>
	);
};

export default GraftsToAssignTable;
